/**
 * MultiplayerService - WebSocket client for 3D multiplayer
 * 
 * Handles:
 * - WebSocket connection with JWT authentication
 * - Scene-based presence (interiors)
 * - World-based presence with spatial relevance (exteriors) - Phase 3
 * - Player state synchronization
 * - Reconnection with exponential backoff
 * - Connection status tracking
 */

// Message types (must match backend)
const MessageType = {
  // Client -> Server
  AUTH: 'auth',
  CHANGE_SCENE: 'change_scene',
  CHANGE_WORLD: 'change_world',  // Phase 3
  PLAYER_UPDATE: 'player_update',
  HEARTBEAT: 'heartbeat',
  CHAT_LOCAL: 'chat_local',
  CHAT_ZONE: 'chat_zone',
  CHAT_DIRECT: 'chat_direct',
  START_CONVERSATION: 'start_conversation',
  END_CONVERSATION: 'end_conversation',
  SIT: 'sit',
  STAND: 'stand',
  
  // Server -> Client
  AUTH_SUCCESS: 'auth_success',
  AUTH_ERROR: 'auth_error',
  SCENE_STATE: 'scene_state',
  WORLD_STATE: 'world_state',  // Phase 3
  PLAYER_JOINED: 'player_joined',
  PLAYER_LEFT: 'player_left',
  PLAYER_UPDATE_BROADCAST: 'player_update',
  CHAT_MESSAGE: 'chat_message',
  CONVERSATION_STARTED: 'conversation_started',
  ERROR: 'error',
};

// Connection states
export const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  AUTHENTICATING: 'authenticating',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
};

// Configuration
const CONFIG = {
  // Update rate for position broadcasts (~15 Hz)
  UPDATE_INTERVAL_MS: 66,
  
  // Heartbeat interval (must be less than server timeout)
  HEARTBEAT_INTERVAL_MS: 25000,
  
  // Reconnection settings
  RECONNECT_BASE_DELAY_MS: 1000,
  RECONNECT_MAX_DELAY_MS: 30000,
  RECONNECT_MULTIPLIER: 2,
};

class MultiplayerService {
  constructor() {
    this.ws = null;
    this.token = null;
    this.playerId = null;
    this.currentSceneId = null;
    this.currentWorldId = null;  // Phase 3: 'midgard' for outdoor world
    
    // Connection state
    this.connectionState = ConnectionState.DISCONNECTED;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    
    // Timers
    this.heartbeatTimer = null;
    this.updateTimer = null;
    
    // Remote players
    this.remotePlayers = new Map();
    
    // Chat messages
    this.localMessages = [];
    this.zoneMessages = {};
    this.directMessages = {};
    this.activeConversations = new Map(); // playerId -> {displayName, username}
    
    // Callbacks
    this.onConnectionStateChange = null;
    this.onRemotePlayersUpdate = null;
    this.onPlayerJoined = null;
    this.onPlayerLeft = null;
    this.onError = null;
    this.onChatMessage = null;
    this.onConversationStarted = null;
    this.heartbeatTimer = null;
    this.updateTimer = null;
    
    // State tracking
    this.remotePlayers = new Map(); // playerId -> PlayerState
    this.pendingUpdate = null; // Pending local player update
    this.lastSentUpdate = null; // Last sent update (for change detection)
    
    // Event callbacks
    this.onConnectionStateChange = null;
    this.onRemotePlayersUpdate = null;
    this.onPlayerJoined = null;
    this.onPlayerLeft = null;
    this.onError = null;
  }
  
  /**
   * Initialize and connect to multiplayer
   * @param {string} token - JWT authentication token
   * @param {string} sceneId - Initial scene to join (or null for world)
   * @param {object} initialPosition - Initial player position
   * @param {object} worldOptions - Phase 3: { worldId, regionId } for outdoor world
   */
  async connect(token, sceneId, initialPosition = { x: 0, y: 1, z: 0 }, worldOptions = null) {
    if (this.connectionState === ConnectionState.CONNECTED || 
        this.connectionState === ConnectionState.CONNECTING ||
        this.connectionState === ConnectionState.AUTHENTICATING) {
      console.warn('MultiplayerService: Already connected or connecting');
      return;
    }
    
    this.token = token;
    this.currentSceneId = sceneId;
    this.initialPosition = initialPosition;
    this.worldOptions = worldOptions;  // Phase 3
    
    await this._connect();
  }
  
  /**
   * Internal connection logic
   */
  async _connect() {
    this._setConnectionState(ConnectionState.CONNECTING);
    
    try {
      // Determine WebSocket URL
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let wsUrl;
      
      if (backendUrl.startsWith('http')) {
        // Convert HTTP URL to WebSocket URL
        wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws/game';
      } else {
        // Same host
        wsUrl = `${wsProtocol}//${window.location.host}/api/ws/game`;
      }
      
      console.log('MultiplayerService: Connecting to', wsUrl);
      
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => this._handleOpen();
      this.ws.onmessage = (event) => this._handleMessage(event);
      this.ws.onclose = (event) => this._handleClose(event);
      this.ws.onerror = (event) => this._handleError(event);
      
    } catch (error) {
      console.error('MultiplayerService: Connection error', error);
      this._scheduleReconnect();
    }
  }
  
  /**
   * Handle WebSocket open
   */
  _handleOpen() {
    console.log('MultiplayerService: WebSocket opened, authenticating...');
    this._setConnectionState(ConnectionState.AUTHENTICATING);
    
    // Send authentication message
    this._send({
      type: MessageType.AUTH,
      token: this.token,
    });
  }
  
  /**
   * Handle incoming WebSocket message
   */
  _handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case MessageType.AUTH_SUCCESS:
          this._handleAuthSuccess(message);
          break;
          
        case MessageType.AUTH_ERROR:
          this._handleAuthError(message);
          break;
          
        case MessageType.SCENE_STATE:
          this._handleSceneState(message);
          break;
        
        case MessageType.WORLD_STATE:  // Phase 3
          this._handleWorldState(message);
          break;
          
        case MessageType.PLAYER_JOINED:
          this._handlePlayerJoined(message);
          break;
          
        case MessageType.PLAYER_LEFT:
          this._handlePlayerLeft(message);
          break;
          
        case MessageType.PLAYER_UPDATE_BROADCAST:
          this._handlePlayerUpdate(message);
          break;
          
        case 'player_sat':
          this._handlePlayerSat(message);
          break;
          
        case 'player_stood':
          this._handlePlayerStood(message);
          break;
        
        case MessageType.CHAT_MESSAGE:
          this._handleChatMessage(message);
          break;
        
        case MessageType.CONVERSATION_STARTED:
          this._handleConversationStarted(message);
          break;
          
        case MessageType.ERROR:
          console.error('MultiplayerService: Server error', message.error);
          if (this.onError) this.onError(message.error);
          break;
          
        default:
          console.warn('MultiplayerService: Unknown message type', message.type);
      }
    } catch (error) {
      console.error('MultiplayerService: Failed to parse message', error);
    }
  }
  
  /**
   * Handle successful authentication
   */
  _handleAuthSuccess(message) {
    console.log('MultiplayerService: Authenticated as', message.playerId);
    this.playerId = message.playerId;
    this.reconnectAttempts = 0;
    this._setConnectionState(ConnectionState.CONNECTED);
    
    // Start heartbeat
    this._startHeartbeat();
    
    // Start update timer
    this._startUpdateTimer();
    
    // Phase 3: Join world or scene based on options
    if (this.worldOptions?.worldId === 'midgard') {
      this._send({
        type: MessageType.CHANGE_WORLD,
        worldId: 'midgard',
        worldPosition: this.initialPosition,
        regionId: this.worldOptions.regionId || 'central-plains',
      });
      this.currentWorldId = 'midgard';
      this.currentSceneId = null;
    } else {
      this._send({
        type: MessageType.CHANGE_SCENE,
        sceneId: this.currentSceneId,
        position: this.initialPosition,
      });
      this.currentWorldId = null;
    }
  }
  
  /**
   * Handle authentication error
   */
  _handleAuthError(message) {
    console.error('MultiplayerService: Auth error', message.error);
    if (this.onError) this.onError(message.error);
    this.disconnect();
  }
  
  /**
   * Validate a position has valid finite numbers
   */
  _isValidPosition(pos) {
    if (!pos || typeof pos !== 'object') return false;
    return (
      typeof pos.x === 'number' && Number.isFinite(pos.x) &&
      typeof pos.y === 'number' && Number.isFinite(pos.y) &&
      typeof pos.z === 'number' && Number.isFinite(pos.z)
    );
  }

  /**
   * Handle scene state (when joining a scene)
   */
  _handleSceneState(message) {
    console.log('MultiplayerService: Scene state received', message.sceneId, message.players?.length || 0, 'players');
    
    this.currentSceneId = message.sceneId;
    this.currentWorldId = null;  // In scene, not world
    
    // Clear existing remote players
    this.remotePlayers.clear();
    
    // Add players from scene state
    for (const player of message.players || []) {
      if (player.playerId !== this.playerId) {
        // Validate position data before adding
        if (!this._isValidPosition(player.position)) {
          console.warn(`MultiplayerService: Skipping player ${player.playerId} with invalid position`, player.position);
          continue;
        }
        
        this.remotePlayers.set(player.playerId, {
          ...player,
          // Add interpolation state
          targetPosition: player.position,
          currentPosition: { ...player.position },
          targetRotation: player.rotation || 0,
          currentRotation: player.rotation || 0,
          lastUpdateTime: Date.now(),
        });
      }
    }
    
    this._notifyRemotePlayersUpdate();
  }
  
  /**
   * Handle world state (Phase 3: when joining outdoor world)
   */
  _handleWorldState(message) {
    console.log('MultiplayerService: World state received', message.worldId, message.players?.length || 0, 'nearby players');
    
    this.currentWorldId = message.worldId;
    this.currentSceneId = null;  // In world, not scene
    
    // Clear existing remote players
    this.remotePlayers.clear();
    
    // Add nearby players from world state
    for (const player of message.players || []) {
      if (player.playerId !== this.playerId) {
        const pos = player.worldPosition || player.position;
        if (!this._isValidPosition(pos)) {
          console.warn(`MultiplayerService: Skipping player ${player.playerId} with invalid position`);
          continue;
        }
        
        this.remotePlayers.set(player.playerId, {
          ...player,
          targetPosition: pos,
          currentPosition: { ...pos },
          targetRotation: player.rotation || 0,
          currentRotation: player.rotation || 0,
          lastUpdateTime: Date.now(),
        });
      }
    }
    
    this._notifyRemotePlayersUpdate();
  }
  
  /**
   * Handle player joined
   */
  _handlePlayerJoined(message) {
    if (message.playerId === this.playerId) return;
    
    // Validate position data before adding
    if (!this._isValidPosition(message.position)) {
      console.warn(`MultiplayerService: Player ${message.displayName} joined with invalid position, using default`);
      message.position = { x: 0, y: 1, z: 0 };
    }
    
    console.log('MultiplayerService: Player joined', message.displayName);
    
    this.remotePlayers.set(message.playerId, {
      ...message,
      targetPosition: message.position,
      currentPosition: { ...message.position },
      targetRotation: message.rotation || 0,
      currentRotation: message.rotation || 0,
      lastUpdateTime: Date.now(),
    });
    
    this._notifyRemotePlayersUpdate();
    if (this.onPlayerJoined) this.onPlayerJoined(message);
  }
  
  /**
   * Handle player left
   */
  _handlePlayerLeft(message) {
    console.log('MultiplayerService: Player left', message.playerId);
    
    this.remotePlayers.delete(message.playerId);
    this._notifyRemotePlayersUpdate();
    if (this.onPlayerLeft) this.onPlayerLeft(message);
  }
  
  /**
   * Handle player position update
   */
  _handlePlayerUpdate(message) {
    if (message.playerId === this.playerId) return;
    
    const player = this.remotePlayers.get(message.playerId);
    if (!player) return;
    
    // Phase 3: Use worldPosition if available, fall back to position
    const newPos = message.worldPosition || message.position;
    
    // Validate position before updating (skip invalid updates)
    if (newPos && !this._isValidPosition(newPos)) {
      console.warn(`MultiplayerService: Ignoring invalid position update for ${message.playerId}`);
      return;
    }
    
    // Update target position/rotation for interpolation
    if (newPos) {
      player.targetPosition = newPos;
      // Also store worldPosition if present
      if (message.worldPosition) {
        player.worldPosition = message.worldPosition;
      }
    }
    if (typeof message.rotation === 'number' && Number.isFinite(message.rotation)) {
      player.targetRotation = message.rotation;
    }
    player.movementState = message.movementState || player.movementState;
    player.lastUpdateTime = Date.now();
    
    this._notifyRemotePlayersUpdate();
  }
  
  /**
   * Handle player sat down event
   */
  _handlePlayerSat(message) {
    console.log('MultiplayerService: Player sat', message.playerId, message.zoneId);
    
    const player = this.remotePlayers.get(message.playerId);
    if (player) {
      player.seatedZoneId = message.zoneId;
      player.seatedSeatId = message.seatId;
      player.movementState = 'seated';
      if (message.position) {
        player.targetPosition = message.position;
        player.currentPosition = { ...message.position };
      }
      if (typeof message.rotation === 'number') {
        player.targetRotation = message.rotation;
        player.currentRotation = message.rotation;
      }
      this._notifyRemotePlayersUpdate();
    }
    
    // Also update if it's the local player (confirmation)
    if (message.playerId === this.playerId) {
      console.log('MultiplayerService: Local player sit confirmed');
    }
  }
  
  /**
   * Handle player stood up event
   */
  _handlePlayerStood(message) {
    console.log('MultiplayerService: Player stood', message.playerId, message.previousZoneId);
    
    const player = this.remotePlayers.get(message.playerId);
    if (player) {
      player.seatedZoneId = null;
      player.seatedSeatId = null;
      player.movementState = 'idle';
      this._notifyRemotePlayersUpdate();
    }
  }
  
  /**
   * Handle incoming chat message
   */
  _handleChatMessage(message) {
    const chatMsg = message.message;
    if (!chatMsg) return;
    
    console.log('MultiplayerService: Chat message', chatMsg.chatType, chatMsg.senderName);
    
    switch (chatMsg.chatType) {
      case 'local':
        this.localMessages.push(chatMsg);
        // Keep only last 50 messages
        if (this.localMessages.length > 50) {
          this.localMessages = this.localMessages.slice(-50);
        }
        break;
        
      case 'zone':
        if (!this.zoneMessages[chatMsg.scopeId]) {
          this.zoneMessages[chatMsg.scopeId] = [];
        }
        this.zoneMessages[chatMsg.scopeId].push(chatMsg);
        if (this.zoneMessages[chatMsg.scopeId].length > 50) {
          this.zoneMessages[chatMsg.scopeId] = this.zoneMessages[chatMsg.scopeId].slice(-50);
        }
        break;
        
      case 'direct':
        // For direct messages, scopeId is the other player's ID
        const otherPlayerId = chatMsg.senderId === this.playerId 
          ? chatMsg.scopeId 
          : chatMsg.senderId;
        
        if (!this.directMessages[otherPlayerId]) {
          this.directMessages[otherPlayerId] = [];
        }
        this.directMessages[otherPlayerId].push(chatMsg);
        if (this.directMessages[otherPlayerId].length > 50) {
          this.directMessages[otherPlayerId] = this.directMessages[otherPlayerId].slice(-50);
        }
        break;
    }
    
    if (this.onChatMessage) {
      this.onChatMessage(chatMsg);
    }
  }
  
  /**
   * Handle conversation started notification
   */
  _handleConversationStarted(message) {
    console.log('MultiplayerService: Conversation started with', message.displayName);
    
    this.activeConversations.set(message.playerId, {
      playerId: message.playerId,
      displayName: message.displayName,
      username: message.username,
    });
    
    if (this.onConversationStarted) {
      this.onConversationStarted(message);
    }
  }
  
  // ========================
  // CHAT SEND METHODS
  // ========================
  
  /**
   * Send local/scene chat message
   */
  sendLocalChat(content) {
    if (!this._isConnected() || !content.trim()) return;
    
    this._send({
      type: MessageType.CHAT_LOCAL,
      content: content.trim(),
    });
  }
  
  /**
   * Send zone chat message
   */
  sendZoneChat(content) {
    if (!this._isConnected() || !content.trim()) return;
    
    this._send({
      type: MessageType.CHAT_ZONE,
      content: content.trim(),
    });
  }
  
  /**
   * Send direct message to another player
   */
  sendDirectChat(recipientId, content) {
    if (!this._isConnected() || !content.trim() || !recipientId) return;
    
    this._send({
      type: MessageType.CHAT_DIRECT,
      recipientId: recipientId,
      content: content.trim(),
    });
  }
  
  /**
   * Start a conversation with another player (Talk)
   */
  startConversation(targetId) {
    if (!this._isConnected() || !targetId) return;
    
    this._send({
      type: MessageType.START_CONVERSATION,
      targetId: targetId,
    });
  }
  
  /**
   * End a conversation with another player
   */
  endConversation(targetId) {
    if (!this._isConnected() || !targetId) return;
    
    this._send({
      type: MessageType.END_CONVERSATION,
      targetId: targetId,
    });
    
    this.activeConversations.delete(targetId);
  }
  
  /**
   * Get local chat messages for current scene
   */
  getLocalMessages() {
    return [...this.localMessages];
  }
  
  /**
   * Get zone chat messages
   */
  getZoneMessages(zoneId) {
    return [...(this.zoneMessages[zoneId] || [])];
  }
  
  /**
   * Get direct messages with a player
   */
  getDirectMessages(playerId) {
    return [...(this.directMessages[playerId] || [])];
  }
  
  /**
   * Get active conversations
   */
  getActiveConversations() {
    return Array.from(this.activeConversations.values());
  }
  
  /**
   * Notify server that player sat down at a zone
   * @param {string} zoneId - Zone ID (e.g., 'golden-mead-hall-bar')
   * @param {string} seatId - Seat ID within the zone
   * @param {object} position - Seat position { x, y, z }
   */
  notifySit(zoneId, seatId, position) {
    if (!this._isConnected()) return;
    
    this._send({
      type: MessageType.SIT,
      zoneId,
      seatId,
      position,
    });
  }
  
  /**
   * Notify server that player stood up
   * @param {string} zoneId - Zone ID the player was sitting at
   */
  notifyStand(zoneId) {
    if (!this._isConnected()) return;
    
    this._send({
      type: 'stand',
      zoneId,
    });
  }
  
  /**
   * Handle WebSocket close
   */
  _handleClose(event) {
    console.log('MultiplayerService: WebSocket closed', event.code, event.reason);
    
    this._stopTimers();
    
    // Don't reconnect if we intentionally disconnected
    if (this.connectionState === ConnectionState.DISCONNECTED) {
      return;
    }
    
    // Schedule reconnection
    this._scheduleReconnect();
  }
  
  /**
   * Handle WebSocket error
   */
  _handleError(event) {
    console.error('MultiplayerService: WebSocket error', event);
    if (this.onError) this.onError('Connection error');
  }
  
  /**
   * Schedule a reconnection attempt
   */
  _scheduleReconnect() {
    this._setConnectionState(ConnectionState.RECONNECTING);
    
    // Clear any existing reconnect timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }
    
    // Calculate delay with exponential backoff
    const delay = Math.min(
      CONFIG.RECONNECT_BASE_DELAY_MS * Math.pow(CONFIG.RECONNECT_MULTIPLIER, this.reconnectAttempts),
      CONFIG.RECONNECT_MAX_DELAY_MS
    );
    
    console.log(`MultiplayerService: Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this._connect();
    }, delay);
  }
  
  /**
   * Disconnect from multiplayer
   */
  disconnect() {
    console.log('MultiplayerService: Disconnecting');
    
    this._setConnectionState(ConnectionState.DISCONNECTED);
    this._stopTimers();
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    
    this.remotePlayers.clear();
    this._notifyRemotePlayersUpdate();
  }
  
  /**
   * Change to a new scene (interior)
   * @param {string} sceneId - Scene to join
   * @param {object} position - Initial position in new scene
   */
  changeScene(sceneId, position = { x: 0, y: 1, z: 0 }) {
    if (this.connectionState !== ConnectionState.CONNECTED) {
      console.warn('MultiplayerService: Not connected, cannot change scene');
      this.currentSceneId = sceneId; // Update for reconnect
      return;
    }
    
    console.log('MultiplayerService: Changing scene to', sceneId);
    this.currentSceneId = sceneId;
    this.currentWorldId = null;  // Entering interior
    
    // Clear remote players (will receive new scene state)
    this.remotePlayers.clear();
    this._notifyRemotePlayersUpdate();
    
    this._send({
      type: MessageType.CHANGE_SCENE,
      sceneId,
      position,
    });
  }
  
  /**
   * Change to outdoor world (Phase 3)
   * @param {object} worldPosition - Position in world coordinates
   * @param {string} regionId - Geographic region
   */
  changeWorld(worldPosition = { x: 0, y: 1, z: 0 }, regionId = 'central-plains') {
    if (this.connectionState !== ConnectionState.CONNECTED) {
      console.warn('MultiplayerService: Not connected, cannot change world');
      return;
    }
    
    console.log('MultiplayerService: Changing to world at', worldPosition);
    this.currentWorldId = 'midgard';
    this.currentSceneId = null;  // Exiting interior
    
    // Clear remote players (will receive new world state)
    this.remotePlayers.clear();
    this._notifyRemotePlayersUpdate();
    
    this._send({
      type: MessageType.CHANGE_WORLD,
      worldId: 'midgard',
      worldPosition,
      regionId,
    });
  }
  
  /**
   * Check if currently in outdoor world
   */
  isInWorld() {
    return this.currentWorldId === 'midgard';
  }
  
  /**
   * Update local player state (called frequently from game loop)
   * Actual sending is throttled by the update timer
   * @param {object} state - { position, rotation, movementState }
   */
  updateLocalPlayer(state) {
    this.pendingUpdate = state;
  }
  
  /**
   * Get current remote players (for rendering)
   * @returns {Array} Array of remote player states
   */
  getRemotePlayers() {
    return Array.from(this.remotePlayers.values());
  }
  
  /**
   * Interpolate remote player positions (call each frame)
   * @param {number} deltaTime - Time since last frame in seconds
   */
  interpolateRemotePlayers(deltaTime) {
    const LERP_FACTOR = 10; // Smoothness factor
    const TELEPORT_THRESHOLD = 10; // Distance to snap instead of lerp
    
    for (const player of this.remotePlayers.values()) {
      // Calculate distance
      const dx = player.targetPosition.x - player.currentPosition.x;
      const dy = player.targetPosition.y - player.currentPosition.y;
      const dz = player.targetPosition.z - player.currentPosition.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      
      if (distance > TELEPORT_THRESHOLD) {
        // Snap to target (teleport/scene change)
        player.currentPosition = { ...player.targetPosition };
        player.currentRotation = player.targetRotation;
      } else {
        // Interpolate position
        const t = Math.min(1, LERP_FACTOR * deltaTime);
        player.currentPosition.x += dx * t;
        player.currentPosition.y += dy * t;
        player.currentPosition.z += dz * t;
        
        // Interpolate rotation (handle wrapping)
        let rotDiff = player.targetRotation - player.currentRotation;
        if (rotDiff > Math.PI) rotDiff -= Math.PI * 2;
        if (rotDiff < -Math.PI) rotDiff += Math.PI * 2;
        player.currentRotation += rotDiff * t;
      }
    }
  }
  
  // ========================
  // Private Helpers
  // ========================
  
  _send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  _isConnected() {
    return !!(this.ws && this.ws.readyState === WebSocket.OPEN);
  }
  
  _setConnectionState(state) {
    if (this.connectionState !== state) {
      console.log('MultiplayerService: State changed', this.connectionState, '->', state);
      this.connectionState = state;
      if (this.onConnectionStateChange) {
        this.onConnectionStateChange(state);
      }
    }
  }
  
  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this._send({ type: MessageType.HEARTBEAT });
    }, CONFIG.HEARTBEAT_INTERVAL_MS);
  }
  
  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
  
  _startUpdateTimer() {
    this._stopUpdateTimer();
    this.updateTimer = setInterval(() => {
      this._sendPendingUpdate();
    }, CONFIG.UPDATE_INTERVAL_MS);
  }
  
  _stopUpdateTimer() {
    if (this.updateTimer) {
      clearInterval(this.updateTimer);
      this.updateTimer = null;
    }
  }
  
  _stopTimers() {
    this._stopHeartbeat();
    this._stopUpdateTimer();
  }
  
  _sendPendingUpdate() {
    if (!this.pendingUpdate || this.connectionState !== ConnectionState.CONNECTED) {
      return;
    }
    
    // Only send if something changed
    if (this.lastSentUpdate) {
      const pos = this.pendingUpdate.position;
      const lastPos = this.lastSentUpdate.position;
      const moved = Math.abs(pos.x - lastPos.x) > 0.01 ||
                   Math.abs(pos.y - lastPos.y) > 0.01 ||
                   Math.abs(pos.z - lastPos.z) > 0.01;
      const rotated = Math.abs(this.pendingUpdate.rotation - this.lastSentUpdate.rotation) > 0.01;
      const stateChanged = this.pendingUpdate.movementState !== this.lastSentUpdate.movementState;
      
      if (!moved && !rotated && !stateChanged) {
        return; // No change, don't send
      }
    }
    
    const updateMsg = {
      type: MessageType.PLAYER_UPDATE,
      position: this.pendingUpdate.position,
      rotation: this.pendingUpdate.rotation,
      movementState: this.pendingUpdate.movementState,
    };
    
    // Phase 3: Add world position if in world
    if (this.currentWorldId === 'midgard') {
      updateMsg.worldId = 'midgard';
      updateMsg.worldPosition = this.pendingUpdate.position;
    }
    
    this._send(updateMsg);
    
    this.lastSentUpdate = { ...this.pendingUpdate };
  }
  
  /**
   * Throttled notification for remote player updates
   * Limits React state updates to max 10/second (100ms intervals)
   */
  _notifyRemotePlayersUpdate() {
    if (!this.onRemotePlayersUpdate) return;
    
    const now = Date.now();
    if (!this._lastNotifyTime) this._lastNotifyTime = 0;
    
    // Throttle to 100ms minimum between notifications
    if (now - this._lastNotifyTime < 100) {
      // Schedule a delayed update if one isn't already scheduled
      if (!this._pendingNotify) {
        this._pendingNotify = setTimeout(() => {
          this._pendingNotify = null;
          this._lastNotifyTime = Date.now();
          if (this.onRemotePlayersUpdate) {
            this.onRemotePlayersUpdate(this.getRemotePlayers());
          }
        }, 100);
      }
      return;
    }
    
    this._lastNotifyTime = now;
    this.onRemotePlayersUpdate(this.getRemotePlayers());
  }
}

// Singleton instance
const multiplayerService = new MultiplayerService();
export default multiplayerService;
